import type { CSSProperties, Component, HTMLAttributes } from 'react';
import React  from 'react';
import { Transition } from 'react-transition-group';
import type { TransitionStatus } from 'react-transition-group';
import type { CustomTransitionProps } from './types';
import type { TransitionProps } from 'react-transition-group/Transition';
import { DEFAULT_TRANSITION_FUNC, getAnimationDurationAsInt } from '../../util/styled';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends CustomTransitionProps, HTMLProps, Partial<Component<TransitionProps>> {}

const Slide = ({ when, children, ...other }: Props): JSX.Element => {
  const default_duration = getAnimationDurationAsInt();
  const default_style: CSSProperties = {
    transition: `visibility 1s linear 300ms, transform ${default_duration}ms ${DEFAULT_TRANSITION_FUNC}`,
    transform: 'translateY(3.5rem)',
    // Not taking room in the DOM
    overflow: 'hidden',
    visibility: 'hidden',
    position: 'absolute',
    height: 0,
    width: '100%',
  };

  const transition_styles: Record<TransitionStatus, CSSProperties> = {
    entering: {
      transform: 'translateY(2.5rem)',
      height: 'auto',
      visibility: 'visible',
      position: 'inherit',
    },
    entered: {
      transform: 'translateY(0)',
      height: 'auto',
      visibility: 'visible',
      position: 'inherit',
      overflow: 'visible',
    },
    exiting: {
      transform: 'translateY(2.5rem)',
      height: 'auto',
      position: 'inherit',
    },
    exited: {
      position: 'inherit',
    },
    unmounted: {},
  };

  return (
    <Transition
      in={when}
      timeout={{ appear: default_duration, enter: default_duration, exit: default_duration }}
      {...(other as HTMLProps & Component<TransitionProps>)}
    >
      {(state) => (
        <div
          style={{
            ...default_style,
            ...transition_styles[state],
          }}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

Slide.displayName = 'Slide';

export default Slide;
